/**
 * TopDecked integration constants shared by the card page module, the importer
 * option, and the admin ingest panel.
 *
 * Display caps MIRROR the backend. The analytics endpoint sorts and caps server
 * side and does NOT echo the caps in its payload, so these three names are kept
 * byte-identical to magictrade-backend src/services/topdecked/constants.ts so a
 * grep on either side finds the coupling. The frontend renders the "TOP N" block
 * markers from these values and applies no ordering or selection of its own.
 */
export const TD_FORMATS_DISPLAYED = 4;
export const TD_DECKS_DISPLAYED = 5;
export const TD_GAMEPLAY_FORMATS_DISPLAYED = 3;

/** Admin ingest panel (ticket 1218427695140081). */
export const TD_INGEST_HISTORY_PAGE_SIZE = 30;
export const TD_INGEST_POLL_MS = 3000;
export const TD_INGEST_POLL_TIMEOUT_MS = 120000;

export type TdPopularity = 'fringe' | 'moderate' | 'popular' | 'staple';
export type TdMomentum = 'crashing' | 'trending_down' | 'stable' | 'trending_up' | 'spiking';
export type TdRate = 'very_low' | 'low' | 'neutral' | 'high' | 'very_high';
export type TdMomentumDirection = 'up' | 'down' | 'none';

export const TD_POPULARITY_LABELS: Record<TdPopularity, string> = {
  fringe: 'Fringe',
  moderate: 'Moderate',
  popular: 'Popular',
  staple: 'Staple',
};

export const TD_RATE_LABELS: Record<TdRate, string> = {
  very_low: 'Very low',
  low: 'Low',
  neutral: 'Neutral',
  high: 'High',
  very_high: 'Very high',
};

/**
 * Momentum is a visual signal, not a number (ticket decision 6). Stable is 68 percent
 * of all values and renders nothing; the row keeps a spacer so alignment holds.
 */
export const TD_MOMENTUM_DIRECTION: Record<TdMomentum, TdMomentumDirection> = {
  spiking: 'up',
  trending_up: 'up',
  stable: 'none',
  trending_down: 'down',
  crashing: 'down',
};

/** One decimal, except a whole number renders bare: 21.8% and 100%, never 100.0%. */
export function formatTdPercentage(value: number): string {
  if (!Number.isFinite(value)) return '0%';
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
}
